import { useMutation, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { AttachmentService, UserService } from "@/services";
import { Nullable } from "@/types/common";
import { UserProfileType } from "@/types/view";
import { EventBusUtils, StorageUtils } from "@/utils";
import { EventConstant, KeyConstant } from "@/const";
import { useSnackbar } from "@/hooks";

type PayloadUploadAvatarMutationType = UserProfileType & {
  file: File;
};

type PayloadUpdateProfileMutationType = UserProfileType & {
  avatar: Nullable<string>;
};

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  const { t: getLabel } = useTranslation();
  const { openSnackbar } = useSnackbar();

  const updateProfileMutation = useMutation({
    mutationFn: (payload: PayloadUpdateProfileMutationType) => UserService.updateUser(payload),
    onSuccess: newProfile => {
      openSnackbar(getLabel("MSG_UPDATE_SUCCESS"), "success");
      queryClient.invalidateQueries({
        queryKey: [KeyConstant.REACT_QUERY.getUserProfile],
      });
      if (newProfile) {
        StorageUtils.setItem(KeyConstant.KEY_NAME, newProfile.name);
        StorageUtils.setItem(KeyConstant.KEY_AVATAR, newProfile.avatar);
        EventBusUtils.emit(EventConstant.PROFILE_EVENTS.updateProfile);
      }
    },
    onError: () => openSnackbar(getLabel("MSG_UPDATE_FAILURE"), "error"),
  });

  const uploadAvatarMutation = useMutation({
    mutationFn: (payload: PayloadUploadAvatarMutationType) => AttachmentService.uploadAttachment(payload.file),
    onSuccess: (newAvatar, variables) => {
      if (newAvatar) {
        updateProfileMutation.mutate({ ...variables, avatar: newAvatar });
      } else {
        openSnackbar(getLabel("MSG_UPDATE_FAILURE"), "error");
      }
    },
    onError: () => openSnackbar(getLabel("MSG_UPDATE_FAILURE"), "error"),
  });

  const updateProfile = (newProfile: UserProfileType) => {
    updateProfileMutation.mutate({ ...newProfile, avatar: null });
  };

  const changeAvatar = (profile: UserProfileType, file: File) => {
    uploadAvatarMutation.mutate({ ...profile, file });
  };

  return {
    updateProfile,
    changeAvatar,
  };
};
